import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, FileText, ShoppingBag, CreditCard, Layers, RotateCw } from 'lucide-react';
import { useState } from 'react';

import type {
  B2bInvoice,
  B2bOrder,
  BusinessAccount,
  Rfq,
  RfqDetail,
  SaasPlan,
  SubscriptionInvoice,
  VendorSubscription,
} from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-border/60 bg-surface-subtle/30 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border p-2 shadow-sm">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`flex-1 rounded-xl px-4 py-2.5 text-[13px] font-bold transition-all sm:flex-none ${
            active === t
              ? 'bg-surface text-ink border-border/60 border shadow-sm'
              : 'text-ink-muted hover:bg-surface-subtle/50 hover:text-ink border border-transparent'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// B2B  (Phase 2.6 — accounts, RFQs, orders, invoices)
// ══════════════════════════════════════════════════════════════════

const ACCT_STATUS_TONE: Record<string, BadgeTone> = {
  PENDING_APPROVAL: 'warning',
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  BLOCKED: 'danger',
};
const RFQ_STATUS_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  QUOTED: 'warning',
  ACCEPTED: 'success',
  REJECTED: 'danger',
  CONVERTED: 'success',
  EXPIRED: 'neutral',
};
const B2B_ORDER_TONE: Record<string, BadgeTone> = {
  DRAFT: 'neutral',
  PENDING_APPROVAL: 'warning',
  CONFIRMED: 'success',
  FULFILLED: 'success',
  CANCELLED: 'danger',
};
const B2B_INVOICE_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  PARTIALLY_PAID: 'warning',
  PAID: 'success',
  OVERDUE: 'danger',
  CANCELLED: 'neutral',
};
const CREDIT_TERMS = ['PREPAID', 'NET_15', 'NET_30', 'NET_60', 'NET_90'] as const;

function AccountsTab() {
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['b2b-accounts'], queryFn: () => api.b2b.listAccounts() });
  const [form, setForm] = useState({
    code: '',
    name: '',
    gstin: '',
    stateCode: '',
    line1: '',
    city: '',
    pincode: '',
    creditTerms: 'NET_30' as (typeof CREDIT_TERMS)[number],
    gstCertificateUrl: '',
    panCardUrl: '',
    creditApplicationUrl: '',
  });

  const [reviewAcct, setReviewAcct] = useState<BusinessAccount | null>(null);
  const [reviewForm, setReviewForm] = useState({
    creditLimit: 0,
    creditTerms: 'NET_30' as (typeof CREDIT_TERMS)[number],
  });

  const create = useMutation({
    mutationFn: () =>
      api.b2b.createAccount({
        code: form.code.toUpperCase(),
        name: form.name.trim(),
        ...(form.gstin ? { gstin: form.gstin.toUpperCase() } : {}),
        stateCode: form.stateCode,
        billingAddress: {
          line1: form.line1,
          city: form.city,
          stateCode: form.stateCode,
          pincode: form.pincode,
        },
        defaultCreditTerms: form.creditTerms,
        ...(form.gstCertificateUrl ? { gstCertificateUrl: form.gstCertificateUrl } : {}),
        ...(form.panCardUrl ? { panCardUrl: form.panCardUrl } : {}),
        ...(form.creditApplicationUrl ? { creditApplicationUrl: form.creditApplicationUrl } : {}),
      }),
    onSuccess: () => {
      setForm({
        code: '',
        name: '',
        gstin: '',
        stateCode: '',
        line1: '',
        city: '',
        pincode: '',
        creditTerms: 'NET_30',
        gstCertificateUrl: '',
        panCardUrl: '',
        creditApplicationUrl: '',
      });
      void qc.invalidateQueries({ queryKey: ['b2b-accounts'] });
    },
  });
  const review = useMutation({
    mutationFn: ({
      id,
      limit,
      terms,
      status,
    }: {
      id: string;
      limit: number;
      terms: any;
      status?: string;
    }) =>
      api.b2b.reviewAccount(id, {
        creditLimitPaise: Math.round(limit * 100),
        creditTerms: terms,
        status: status as any,
      }),
    onSuccess: () => {
      setReviewAcct(null);
      void qc.invalidateQueries({ queryKey: ['b2b-accounts'] });
    },
  });
  const transition = useMutation({
    mutationFn: ({ id, status }: { id: string; status: BusinessAccount['status'] }) =>
      api.b2b.updateAccount(id, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['b2b-accounts'] }),
  });

  return (
    <div className="space-y-6">
      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Building2 className="h-4 w-4" /> New Business Account
        </h3>
        <form
          className="grid gap-5 sm:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (form.code && form.name && form.stateCode && form.line1 && form.city && form.pincode)
              create.mutate();
          }}
        >
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            required
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="GSTIN"
            value={form.gstin}
            onChange={(e) => setForm((f) => ({ ...f, gstin: e.target.value.toUpperCase() }))}
          />
          <Input
            label="State Code"
            value={form.stateCode}
            onChange={(e) => setForm((f) => ({ ...f, stateCode: e.target.value }))}
            required
          />
          <Input
            label="Address Line 1"
            value={form.line1}
            onChange={(e) => setForm((f) => ({ ...f, line1: e.target.value }))}
            required
          />
          <Input
            label="City"
            value={form.city}
            onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
            required
          />
          <Input
            label="Pincode"
            value={form.pincode}
            onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value }))}
            required
          />
          <div className="sm:col-span-2">
            <label className="text-ink mb-1 block text-[13px] font-bold">Credit Terms</label>
            <select
              className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 h-10 w-full rounded-xl border px-3 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
              value={form.creditTerms}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  creditTerms: e.target.value as (typeof CREDIT_TERMS)[number],
                }))
              }
            >
              {CREDIT_TERMS.map((c) => (
                <option key={c} value={c}>
                  {c.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="GST Certificate URL (Optional)"
            value={form.gstCertificateUrl}
            onChange={(e) => setForm((f) => ({ ...f, gstCertificateUrl: e.target.value }))}
          />
          <Input
            label="PAN Card URL (Optional)"
            value={form.panCardUrl}
            onChange={(e) => setForm((f) => ({ ...f, panCardUrl: e.target.value }))}
          />
          <Input
            label="Credit App URL (Optional)"
            value={form.creditApplicationUrl}
            onChange={(e) => setForm((f) => ({ ...f, creditApplicationUrl: e.target.value }))}
          />
          <div className="border-border/40 mt-2 flex items-center justify-end border-t pt-4 sm:col-span-3">
            <Button type="submit" loading={create.isPending} className="px-8">
              Create Account
            </Button>
          </div>
        </form>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {accounts.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (accounts.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<Building2 className="text-ink-muted h-10 w-10" />}
              title="No business accounts"
              description="Create your first B2B account above."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Code</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Credit Limit</th>
                  <th className="px-4 py-3">Outstanding</th>
                  <th className="px-4 py-3">Terms</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(accounts.data ?? []).map((a: BusinessAccount) => (
                  <tr key={a.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">{a.code}</td>
                    <td className="px-4 py-3 font-semibold">{a.name}</td>
                    <td className="text-ink px-4 py-3 font-semibold">
                      {formatPaise(a.creditLimitPaise)}
                    </td>
                    <td className="text-ink px-4 py-3 font-semibold">
                      {formatPaise(a.outstandingPaise)}
                    </td>
                    <td className="px-4 py-3 text-[12px]">
                      {a.defaultCreditTerms.replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone={ACCT_STATUS_TONE[a.status] ?? 'neutral'}>
                        {a.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 pr-5 text-right">
                      <div className="flex justify-end gap-2">
                        {a.status === 'PENDING_APPROVAL' && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setReviewAcct(a);
                              setReviewForm({ creditLimit: 0, creditTerms: a.defaultCreditTerms });
                            }}
                          >
                            Review & Approve
                          </Button>
                        )}
                        {a.status === 'ACTIVE' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => transition.mutate({ id: a.id, status: 'SUSPENDED' })}
                          >
                            Suspend
                          </Button>
                        )}
                        {a.status === 'SUSPENDED' && (
                          <Button
                            size="sm"
                            onClick={() => transition.mutate({ id: a.id, status: 'ACTIVE' })}
                          >
                            Reactivate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {reviewAcct && (
        <div className="bg-ink/50 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-surface border-border/60 w-full max-w-lg rounded-2xl border p-6 shadow-xl">
            <h3 className="text-ink mb-4 text-lg font-bold">Review Account: {reviewAcct.name}</h3>
            <div className="mb-6 space-y-4">
              <div>
                <span className="text-ink-muted mb-1 block text-sm font-semibold">Documents</span>
                {reviewAcct.gstCertificateUrl ? (
                  <a
                    href={reviewAcct.gstCertificateUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-gold-500 block text-sm hover:underline"
                  >
                    GST Certificate
                  </a>
                ) : (
                  <span className="text-ink-muted block text-sm">No GST Cert</span>
                )}
                {reviewAcct.panCardUrl ? (
                  <a
                    href={reviewAcct.panCardUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-gold-500 block text-sm hover:underline"
                  >
                    PAN Card
                  </a>
                ) : (
                  <span className="text-ink-muted block text-sm">No PAN</span>
                )}
                {reviewAcct.creditApplicationUrl ? (
                  <a
                    href={reviewAcct.creditApplicationUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-gold-500 block text-sm hover:underline"
                  >
                    Credit Application
                  </a>
                ) : (
                  <span className="text-ink-muted block text-sm">No Credit App</span>
                )}
              </div>
              <Input
                type="number"
                label="Credit Limit (₹)"
                value={reviewForm.creditLimit}
                onChange={(e) =>
                  setReviewForm((f) => ({ ...f, creditLimit: Number(e.target.value) }))
                }
              />
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">Credit Terms</label>
                <select
                  className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 h-10 w-full rounded-xl border px-3 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
                  value={reviewForm.creditTerms}
                  onChange={(e) =>
                    setReviewForm((f) => ({ ...f, creditTerms: e.target.value as any }))
                  }
                >
                  {CREDIT_TERMS.map((c) => (
                    <option key={c} value={c}>
                      {c.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setReviewAcct(null)}>
                Cancel
              </Button>
              <Button
                onClick={() =>
                  review.mutate({
                    id: reviewAcct.id,
                    limit: reviewForm.creditLimit,
                    terms: reviewForm.creditTerms,
                    status: 'REJECTED',
                  })
                }
              >
                Reject
              </Button>
              <Button
                loading={review.isPending}
                onClick={() =>
                  review.mutate({
                    id: reviewAcct.id,
                    limit: reviewForm.creditLimit,
                    terms: reviewForm.creditTerms,
                    status: 'ACTIVE',
                  })
                }
              >
                Approve
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RfqsTab() {
  const qc = useQueryClient();
  const rfqs = useQuery({ queryKey: ['b2b-rfqs'], queryFn: () => api.b2b.listRfqs() });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [quoteLines, setQuoteLines] = useState<Record<string, string>>({});
  const detail = useQuery<RfqDetail>({
    queryKey: ['b2b-rfq', expanded],
    queryFn: () => api.b2b.getRfq(expanded!),
    enabled: !!expanded,
  });

  const quote = useMutation({
    mutationFn: () => {
      const lines = (detail.data?.items ?? [])
        .filter((it) => quoteLines[it.id] && Number(quoteLines[it.id]) > 0)
        .map((it) => ({
          rfqItemId: it.id,
          quotedUnitPricePaise: Math.round(Number(quoteLines[it.id]) * 100),
        }));
      return api.b2b.quoteRfq(expanded!, { lines });
    },
    onSuccess: () => {
      setQuoteLines({});
      void qc.invalidateQueries({ queryKey: ['b2b-rfqs'] });
      void qc.invalidateQueries({ queryKey: ['b2b-rfq', expanded] });
    },
  });

  if (rfqs.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );

  if ((rfqs.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<FileText className="text-ink-muted h-10 w-10" />}
          title="No RFQs"
          description="Buyer RFQs will appear here."
        />
      </div>
    );

  return (
    <div className="space-y-4">
      {(rfqs.data ?? []).map((r: Rfq) => (
        <div
          key={r.id}
          className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm transition-all hover:shadow-md"
        >
          <div className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[14px] font-bold">{r.rfqNumber}</span>
                  <Badge tone={RFQ_STATUS_TONE[r.status] ?? 'neutral'}>{r.status}</Badge>
                </div>
                <div className="text-ink-subtle mt-1 text-[12px] font-medium">
                  {new Date(r.createdAt).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={expanded === r.id ? 'ghost' : 'outline'}
                  onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                >
                  {expanded === r.id ? 'Hide Details' : 'View RFQ'}
                </Button>
              </div>
            </div>
            {expanded === r.id && (
              <div className="border-border/40 mt-5 border-t pt-5">
                {detail.isLoading ? (
                  <div className="grid h-20 place-items-center">
                    <Spinner size="sm" />
                  </div>
                ) : detail.data ? (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                            <th className="pb-3 pr-4">Item Description</th>
                            <th className="pb-3 pr-4 text-center">Qty</th>
                            <th className="pb-3 pr-4 text-right">Requested</th>
                            <th className="pb-3 pr-4 text-right">Quoted</th>
                            {detail.data.status === 'SUBMITTED' && (
                              <th className="pb-3 pr-4">Quote ₹</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-border/40 divide-y text-[13px]">
                          {detail.data.items.map((it) => (
                            <tr key={it.id} className="group">
                              <td className="py-3 pr-4 font-semibold">{it.description}</td>
                              <td className="py-3 pr-4 text-center font-medium">{it.qty}</td>
                              <td className="py-3 pr-4 text-right font-medium">
                                {it.requestedUnitPricePaise != null
                                  ? formatPaise(it.requestedUnitPricePaise)
                                  : '—'}
                              </td>
                              <td className="text-ink py-3 pr-4 text-right font-bold">
                                {it.quotedUnitPricePaise != null
                                  ? formatPaise(it.quotedUnitPricePaise)
                                  : '—'}
                              </td>
                              {detail.data.status === 'SUBMITTED' && (
                                <td className="py-3 pr-4">
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 w-28 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors focus:outline-none focus:ring-1"
                                    value={quoteLines[it.id] ?? ''}
                                    placeholder="Amount"
                                    onChange={(e) =>
                                      setQuoteLines((q) => ({ ...q, [it.id]: e.target.value }))
                                    }
                                  />
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {detail.data.status === 'SUBMITTED' && (
                      <div className="mt-5 flex justify-end">
                        <Button onClick={() => quote.mutate()} loading={quote.isPending}>
                          Submit Quote
                        </Button>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function B2bOrdersTab() {
  const qc = useQueryClient();
  const orders = useQuery({ queryKey: ['b2b-orders'], queryFn: () => api.b2b.listOrders() });
  const warehouses = useQuery({
    queryKey: ['erp-warehouses'],
    queryFn: () => api.erp.listWarehouses(),
  });
  const [warehouseSel, setWarehouseSel] = useState<Record<string, string>>({});

  const approve = useMutation({
    mutationFn: ({ id, warehouseId }: { id: string; warehouseId: string }) =>
      api.b2b.approveOrder(id, { warehouseId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['b2b-orders'] }),
  });
  const fulfill = useMutation({
    mutationFn: (id: string) => api.b2b.fulfillOrder(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['b2b-orders'] }),
  });

  if (orders.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((orders.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<ShoppingBag className="text-ink-muted h-10 w-10" />}
          title="No B2B orders"
          description="Wholesale orders will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Order #</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Terms</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(orders.data ?? []).map((o: B2bOrder) => (
              <tr key={o.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                  {o.orderNumber}
                </td>
                <td className="text-ink px-4 py-3 font-semibold">
                  {formatPaise(o.grandTotalPaise)}
                </td>
                <td className="px-4 py-3 text-[12px]">{o.creditTerms.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-center">
                  <Badge tone={B2B_ORDER_TONE[o.status] ?? 'neutral'}>
                    {o.status.replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td className="px-4 py-3 pr-5 text-right">
                  <div className="flex justify-end gap-2">
                    {o.status === 'PENDING_APPROVAL' && (
                      <div className="flex items-center gap-2">
                        <select
                          className="border-border/60 bg-surface text-ink focus:border-gold-400 focus:ring-gold-400 rounded-lg border px-2 py-1.5 text-[12px] font-medium transition-colors focus:outline-none focus:ring-1"
                          value={warehouseSel[o.id] ?? ''}
                          onChange={(e) =>
                            setWarehouseSel((w) => ({ ...w, [o.id]: e.target.value }))
                          }
                        >
                          <option value="">Warehouse…</option>
                          {(warehouses.data ?? []).map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.code}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          onClick={() =>
                            warehouseSel[o.id] &&
                            approve.mutate({ id: o.id, warehouseId: warehouseSel[o.id]! })
                          }
                        >
                          Approve
                        </Button>
                      </div>
                    )}
                    {o.status === 'CONFIRMED' && (
                      <Button size="sm" onClick={() => fulfill.mutate(o.id)}>
                        Fulfill
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function B2bInvoicesTab() {
  const qc = useQueryClient();
  const invoices = useQuery({ queryKey: ['b2b-invoices'], queryFn: () => api.b2b.listInvoices() });

  const recordPayment = useMutation({
    mutationFn: (inv: B2bInvoice) =>
      api.b2b.recordPayment(inv.id, {
        amountPaise: inv.totalPaise - inv.paidPaise,
        method: 'BANK_TRANSFER',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['b2b-invoices'] }),
  });

  if (invoices.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((invoices.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<CreditCard className="text-ink-muted h-10 w-10" />}
          title="No B2B invoices"
          description="Invoices will appear here once orders are fulfilled."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Invoice #</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Paid</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(invoices.data ?? []).map((inv: B2bInvoice) => (
              <tr key={inv.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                  {inv.invoiceNumber}
                </td>
                <td className="text-ink px-4 py-3 text-right font-semibold">
                  {formatPaise(inv.totalPaise)}
                </td>
                <td className="text-ink px-4 py-3 text-right font-semibold">
                  {formatPaise(inv.paidPaise)}
                </td>
                <td className="px-4 py-3 text-[12px] font-medium">
                  {new Date(inv.dueAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge tone={B2B_INVOICE_TONE[inv.status] ?? 'neutral'}>
                    {inv.status.replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td className="px-4 py-3 pr-5 text-right">
                  <div className="flex justify-end gap-2">
                    {(inv.status === 'PENDING' ||
                      inv.status === 'PARTIALLY_PAID' ||
                      inv.status === 'OVERDUE') && (
                      <Button size="sm" onClick={() => recordPayment.mutate(inv)}>
                        Record full payment
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PricingContractsTab() {
  const qc = useQueryClient();
  const contracts = useQuery({
    queryKey: ['b2b-contracts'],
    queryFn: () => api.b2b.listContracts(),
  });

  const review = useMutation({
    mutationFn: ({
      id,
      status,
      rejectedReason,
    }: {
      id: string;
      status: 'ACTIVE' | 'REJECTED';
      rejectedReason?: string;
    }) =>
      api.b2b.updateContractStatus(id, { status, ...(rejectedReason ? { rejectedReason } : {}) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['b2b-contracts'] }),
  });

  if (contracts.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((contracts.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<FileText className="text-ink-muted h-10 w-10" />}
          title="No contracts"
          description="B2B pricing contracts will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Variant ID</th>
              <th className="px-4 py-3">Account ID</th>
              <th className="px-4 py-3 text-right">Unit Price</th>
              <th className="px-4 py-3 text-center">Min Qty</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(contracts.data ?? []).map((c: any) => (
              <tr key={c.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                  {c.variantId}
                </td>
                <td className="px-4 py-3 font-mono text-[12px] font-semibold">
                  {c.businessAccountId}
                </td>
                <td className="text-ink px-4 py-3 text-right font-semibold">
                  {formatPaise(c.unitPricePaise)}
                </td>
                <td className="px-4 py-3 text-center text-[12px] font-medium">{c.minOrderQty}</td>
                <td className="px-4 py-3 text-center">
                  <Badge
                    tone={
                      c.status === 'ACTIVE'
                        ? 'success'
                        : c.status === 'PENDING_APPROVAL'
                          ? 'warning'
                          : 'neutral'
                    }
                  >
                    {c.status}
                  </Badge>
                </td>
                <td className="px-4 py-3 pr-5 text-right">
                  <div className="flex justify-end gap-2">
                    {c.status === 'PENDING_APPROVAL' && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => review.mutate({ id: c.id, status: 'REJECTED' })}
                        >
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => review.mutate({ id: c.id, status: 'ACTIVE' })}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export function B2bPage() {
  const [tab, setTab] = useState('Accounts');
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">B2B Wholesale</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Business accounts, RFQs, wholesale orders and credit invoices.
          </p>
        </div>
      </div>
      <Tabs
        tabs={['Accounts', 'Pricing Contracts', 'RFQs', 'Orders', 'Invoices']}
        active={tab}
        onChange={setTab}
      />
      {tab === 'Accounts' && <AccountsTab />}
      {tab === 'Pricing Contracts' && <PricingContractsTab />}
      {tab === 'RFQs' && <RfqsTab />}
      {tab === 'Orders' && <B2bOrdersTab />}
      {tab === 'Invoices' && <B2bInvoicesTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// SAAS  (Phase 2.7 — plans, subscriptions, invoices)
// ══════════════════════════════════════════════════════════════════

const SUB_STATUS_TONE: Record<string, BadgeTone> = {
  TRIALING: 'warning',
  ACTIVE: 'success',
  PAST_DUE: 'danger',
  CANCELLED: 'neutral',
  EXPIRED: 'neutral',
};
const SAAS_INVOICE_TONE: Record<string, BadgeTone> = {
  PENDING: 'warning',
  PAID: 'success',
  FAILED: 'danger',
  REFUNDED: 'neutral',
  CANCELLED: 'neutral',
};

function PlansTab() {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ['saas-plans'], queryFn: () => api.saas.listPlans() });
  const [editingPlan, setEditingPlan] = useState<SaasPlan | null>(null);

  if (plans.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((plans.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<Layers className="text-ink-muted h-10 w-10" />}
          title="No plans"
          description="SaaS plans will appear here."
        />
      </div>
    );

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {(plans.data ?? []).map((p: SaasPlan) => (
        <div
          key={p.id}
          className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm transition-all hover:-translate-y-1 hover:shadow-md"
        >
          <div className="p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-ink text-[14px] font-bold">{p.name}</h3>
              {p.active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Off</Badge>}
            </div>
            <div className="mt-4">
              <p className="text-ink text-2xl font-bold tracking-tight">
                {formatPaise(p.monthlyPricePaise)}
                <span className="text-ink-subtle text-[12px] font-medium"> / mo</span>
              </p>
              <p className="text-ink-muted mt-0.5 text-[12px] font-medium">
                {formatPaise(p.annualPricePaise)} / yr
              </p>
            </div>
            <div className="border-border/40 mt-5 space-y-2 border-t pt-4">
              <div className="flex justify-between text-[12px]">
                <span className="text-ink-muted font-medium">Product cap</span>
                <span className="text-ink font-bold">
                  {p.productCap === -1 ? 'Unlimited' : p.productCap}
                </span>
              </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-ink-muted font-medium">Media/product</span>
                <span className="text-ink font-bold">
                  {p.mediaPerProductCap === -1 ? 'Unlimited' : p.mediaPerProductCap}
                </span>
              </div>
              <div className="flex justify-between text-[12px]">
                <span className="text-ink-muted font-medium">Sponsored fee</span>
                <span className="text-ink font-bold">{(p.sponsoredFeeBps / 100).toFixed(2)}%</span>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {Object.entries(p.features)
                .filter(([, v]) => v)
                .map(([k]) => (
                  <span
                    key={k}
                    className="bg-surface-subtle text-ink-muted rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                  >
                    {k.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                ))}
            </div>
            <div className="border-border/40 mt-4 border-t pt-4">
              <Button
                size="sm"
                variant="outline"
                className="w-full"
                onClick={() => setEditingPlan(p)}
              >
                Edit Plan
              </Button>
            </div>
          </div>
        </div>
      ))}
      {editingPlan && (
        <EditPlanModal
          plan={editingPlan}
          onClose={() => setEditingPlan(null)}
          onSuccess={() => {
            setEditingPlan(null);
            void qc.invalidateQueries({ queryKey: ['saas-plans'] });
          }}
        />
      )}
    </div>
  );
}

function EditPlanModal({
  plan,
  onClose,
  onSuccess,
}: {
  plan: SaasPlan;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [data, setData] = useState({
    name: plan.name,
    monthlyPricePaise: plan.monthlyPricePaise / 100,
    annualPricePaise: plan.annualPricePaise / 100,
    productCap: plan.productCap,
    mediaPerProductCap: plan.mediaPerProductCap,
    sponsoredFeeBps: plan.sponsoredFeeBps,
    features: JSON.stringify(plan.features, null, 2),
    active: plan.active,
  });
  const [error, setError] = useState('');

  const save = useMutation({
    mutationFn: async () => {
      let parsedFeatures;
      try {
        parsedFeatures = JSON.parse(data.features);
      } catch (e) {
        throw new Error('Features must be valid JSON');
      }
      return api.saas.upsertPlan({
        code: plan.code,
        name: data.name,
        monthlyPricePaise: Math.floor(data.monthlyPricePaise * 100),
        annualPricePaise: Math.floor(data.annualPricePaise * 100),
        productCap: Number(data.productCap),
        mediaPerProductCap: Number(data.mediaPerProductCap),
        sponsoredFeeBps: Number(data.sponsoredFeeBps),
        features: parsedFeatures,
        active: data.active,
      });
    },
    onSuccess,
    onError: (err: any) => setError(err.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface border-border flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border shadow-xl">
        <div className="border-border/40 flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-ink text-lg font-bold">Edit {plan.name} Plan</h2>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-5">
          {error && <div className="text-danger-500 text-sm font-medium">{error}</div>}
          <div>
            <label className="text-ink text-sm font-bold">Name</label>
            <Input value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-ink text-sm font-bold">Monthly Price (INR)</label>
              <Input
                type="number"
                value={data.monthlyPricePaise}
                onChange={(e) =>
                  setData({ ...data, monthlyPricePaise: parseFloat(e.target.value) })
                }
              />
            </div>
            <div>
              <label className="text-ink text-sm font-bold">Annual Price (INR)</label>
              <Input
                type="number"
                value={data.annualPricePaise}
                onChange={(e) => setData({ ...data, annualPricePaise: parseFloat(e.target.value) })}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-ink text-sm font-bold">Product Cap (-1 = unlimited)</label>
              <Input
                type="number"
                value={data.productCap}
                onChange={(e) => setData({ ...data, productCap: parseInt(e.target.value, 10) })}
              />
            </div>
            <div>
              <label className="text-ink text-sm font-bold">Media/Product Cap</label>
              <Input
                type="number"
                value={data.mediaPerProductCap}
                onChange={(e) =>
                  setData({ ...data, mediaPerProductCap: parseInt(e.target.value, 10) })
                }
              />
            </div>
          </div>
          <div>
            <label className="text-ink text-sm font-bold">Sponsored Fee (bps)</label>
            <Input
              type="number"
              value={data.sponsoredFeeBps}
              onChange={(e) => setData({ ...data, sponsoredFeeBps: parseInt(e.target.value, 10) })}
            />
          </div>
          <div>
            <label className="text-ink text-sm font-bold">Features (JSON)</label>
            <textarea
              className="border-border bg-surface focus:ring-primary/50 w-full rounded-lg border p-2 font-mono text-sm focus:ring-2"
              rows={6}
              value={data.features}
              onChange={(e) => setData({ ...data, features: e.target.value })}
            />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              checked={data.active}
              onChange={(e) => setData({ ...data, active: e.target.checked })}
              className="border-border text-primary focus:ring-primary/50 rounded"
            />
            <label className="text-ink text-sm font-bold">Active</label>
          </div>
        </div>
        <div className="bg-surface-subtle border-border/40 flex justify-end gap-3 border-t px-5 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function SubscriptionsTab() {
  const qc = useQueryClient();
  const subs = useQuery({
    queryKey: ['saas-subs'],
    queryFn: () => api.saas.adminListSubscriptions(),
  });
  const plans = useQuery({ queryKey: ['saas-plans'], queryFn: () => api.saas.listPlans() });

  const runRenewals = useMutation({
    mutationFn: () => api.saas.runRenewals(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['saas-subs'] });
      void qc.invalidateQueries({ queryKey: ['saas-admin-invoices'] });
    },
  });

  const planName = (planId: string) =>
    plans.data?.find((p) => p.id === planId)?.name ?? planId.slice(0, 8);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button
          onClick={() => runRenewals.mutate()}
          loading={runRenewals.isPending}
          className="gap-2"
        >
          <RotateCw className="h-4 w-4" /> Run Renewals
        </Button>
      </div>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {subs.isLoading ? (
          <div className="grid min-h-[300px] place-items-center">
            <Spinner size="lg" />
          </div>
        ) : (subs.data ?? []).length === 0 ? (
          <div className="py-12">
            <EmptyState
              icon={<RotateCw className="text-ink-muted h-10 w-10" />}
              title="No subscriptions"
              description="Vendor subscriptions will appear here."
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
                  <th className="px-4 py-3 pl-5">Vendor</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Cycle</th>
                  <th className="px-4 py-3">Period End</th>
                  <th className="px-4 py-3 text-center">Auto-renew</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 pr-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-border/40 divide-y text-[13px]">
                {(subs.data ?? []).map((s: VendorSubscription) => (
                  <tr key={s.id} className="hover:bg-surface-subtle/30 group transition-colors">
                    <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                      {s.vendorId.slice(0, 12)}…
                    </td>
                    <td className="px-4 py-3 font-semibold">{planName(s.planId)}</td>
                    <td className="px-4 py-3 text-[12px]">{s.billingCycle}</td>
                    <td className="px-4 py-3 text-[12px] font-medium">
                      {new Date(s.currentPeriodEnd).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-center font-medium">
                      {s.autoRenew ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge tone={SUB_STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>
                    </td>
                    <td className="px-4 py-3 pr-5 text-right">
                      {s.status === 'PAST_DUE' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            api.saas
                              .adminRecoverSubscription(s.vendorId)
                              .then(() => qc.invalidateQueries({ queryKey: ['saas-subs'] }))
                          }
                        >
                          Recover
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SaasInvoicesTab() {
  const qc = useQueryClient();
  const invoices = useQuery({
    queryKey: ['saas-admin-invoices'],
    queryFn: () => api.saas.adminListInvoices(),
  });

  const pay = useMutation({
    mutationFn: (id: string) => api.saas.payInvoice(id, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['saas-admin-invoices'] }),
  });

  if (invoices.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((invoices.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<CreditCard className="text-ink-muted h-10 w-10" />}
          title="No invoices"
          description="Subscription invoices will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Invoice #</th>
              <th className="px-4 py-3">Period</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3">Due</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(invoices.data ?? []).map((inv: SubscriptionInvoice) => (
              <tr key={inv.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                  {inv.invoiceNumber}
                </td>
                <td className="px-4 py-3 text-[12px] font-medium">
                  {new Date(inv.periodStart).toLocaleDateString()} –{' '}
                  {new Date(inv.periodEnd).toLocaleDateString()}
                </td>
                <td className="text-ink px-4 py-3 text-right font-semibold">
                  {formatPaise(inv.totalPaise)}
                </td>
                <td className="px-4 py-3 text-[12px] font-medium">
                  {new Date(inv.dueAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge tone={SAAS_INVOICE_TONE[inv.status] ?? 'neutral'}>{inv.status}</Badge>
                </td>
                <td className="px-4 py-3 pr-5 text-right">
                  <div className="flex justify-end gap-2">
                    {(inv.status === 'PENDING' || inv.status === 'FAILED') && (
                      <Button size="sm" onClick={() => pay.mutate(inv.id)}>
                        Mark Paid
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SaasPage() {
  const [tab, setTab] = useState('Plans');
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Vendor SaaS</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Subscription plans, vendor subscriptions and billing invoices.
          </p>
        </div>
      </div>
      <Tabs tabs={['Plans', 'Subscriptions', 'Invoices']} active={tab} onChange={setTab} />
      {tab === 'Plans' && <PlansTab />}
      {tab === 'Subscriptions' && <SubscriptionsTab />}
      {tab === 'Invoices' && <SaasInvoicesTab />}
    </div>
  );
}
